import { AuthRoute } from 'guards/AuthRoute';
import { PublicRoute } from 'guards/PublicRoute';
import { useFeatureSelector } from 'guards/useFeatureSelector';
import { isUserReadOnlySelector } from 'store/selectors/userSelectors/isUserReadOnlySelector';
import { useSelector } from 'react-redux';
import { useHasMarketplaceSelector } from 'store/selectors/organizationSelectors/orgSettingsSelector';
import {
  AgencyTeamMemberPage,
  CommunicationPage,
  CreateShiftsPage,
  DashboardPage,
  ForgotPage,
  LoginPage,
  NotFoundPage,
  PendingPage,
  RegistrationPage,
  ReportsPage,
  SchedulePage,
  ShiftsPage,
  TeamMemberPage,
  TimesheetsPage,
} from 'pages';
import SettingsPage from 'pages/Settings/SettingsPage';
import { CustomFieldsPage } from 'pages/Settings/pages/CustomFields';
import { FC, ReactElement } from 'react';
import { SwitchProps } from 'react-router-dom';
import { AnimatedRoutes } from 'routes';
import { withMemo } from 'utils/withMemo';
import { Version } from 'components/Version';
import Auth from 'pages/Settings/pages/ADP/ADPAuthRedirectPage/ADPIntegration/Auth';

export const Routes: FC = withMemo(function _Routes(): ReactElement<SwitchProps> {
  const isYMCAReadOnly = useFeatureSelector('FEATURE_TYPE_YMCA_READ_ONLY_SECONDARY_USER');
  const isSecondaryUserReadOnly = useSelector(isUserReadOnlySelector);
  const isCustomFieldsEnabled = useFeatureSelector('FEATURE_TYPE_CUSTOM_FIELDS');
  const hasMarketplaceAccess = useHasMarketplaceSelector();
  return (
    <AnimatedRoutes exitBeforeEnter initial={false}>
      <PublicRoute exact path="/forgot" component={ForgotPage} />
      <PublicRoute exact path="/login" component={LoginPage} />
      <PublicRoute exact path="/register" component={RegistrationPage} />
      <PublicRoute exact path="/version" component={Version} />
      {!isSecondaryUserReadOnly && (
        <AuthRoute path="/communications" component={CommunicationPage} />
      )}
      {!hasMarketplaceAccess && (
        <AuthRoute
          userType={[
            'USER_TYPE_B2B',
            'USER_TYPE_PREMIUM_SECONDARY',
            'USER_TYPE_ESSENTIAL_SECONDARY',
          ]}
          path="/reports"
          component={ReportsPage}
        />
      )}
      <AuthRoute exact path="/pending" component={PendingPage} />
      {!isYMCAReadOnly && !isSecondaryUserReadOnly && (
        <AuthRoute
          userType={[
            'USER_TYPE_B2B',
            'USER_TYPE_PREMIUM_SECONDARY',
            'USER_TYPE_ESSENTIAL_SECONDARY',
          ]}
          exact
          path={['/create-shifts', '/create-shift']}
          component={CreateShiftsPage}
        />
      )}
      {!isYMCAReadOnly && <AuthRoute path="/schedule" component={SchedulePage} />}
      <AuthRoute
        userType={[
          'USER_TYPE_AGENCY',
          'USER_TYPE_B2B',
          'USER_TYPE_PREMIUM_SECONDARY',
          'USER_TYPE_ESSENTIAL_SECONDARY',
        ]}
        exact
        path="/team-member"
        component={TeamMemberPage}
      />
      {!isSecondaryUserReadOnly && !hasMarketplaceAccess && (
        <AuthRoute
          userType={[
            'USER_TYPE_B2B',
            'USER_TYPE_PREMIUM_SECONDARY',
            'USER_TYPE_ESSENTIAL_SECONDARY',
          ]}
          path="/agency-member"
          exact={true}
          component={AgencyTeamMemberPage}
        />
      )}
      {isCustomFieldsEnabled && !isSecondaryUserReadOnly && (
        <AuthRoute exact path="/settings/company" component={CustomFieldsPage} />
      )}
      {!isSecondaryUserReadOnly && <AuthRoute path="/settings" component={SettingsPage} />}
      <AuthRoute exact path="/auth" component={Auth} />
      <AuthRoute exact path="/timesheets" component={TimesheetsPage} />
      <AuthRoute
        exact
        path={['/shifts', '/shifts/calendar', '/shifts/list']}
        component={ShiftsPage}
      />
      <AuthRoute path={['/', '/dashboard']} component={DashboardPage} />
      <AuthRoute component={NotFoundPage} />
    </AnimatedRoutes>
  );
});
